import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Arrow } from "./icons";
import { PHOTOS } from "../data/photos";

// One reveal system for the whole site. Anything with data-reveal gets
// .in once it scrolls into view; the CSS decides how it moves:
//   data-reveal         lift (fade + rise)
//   data-reveal="words" word-by-word headline reveal
//   data-reveal="photo" frame wipe + image settle
// The pre-states only exist under html.motion (set in index.js when the
// visitor hasn't asked for reduced motion), so without it nothing hides.
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll("[data-reveal]");
    if (!document.documentElement.classList.contains("motion") || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
      }),
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

// Responsive, layout-stable photo. `name` is a key of PHOTOS.
export function Photo({ name, sizes = "(max-width: 860px) 100vw, 50vw", priority = false, className = "", reveal = true }) {
  const p = PHOTOS[name];
  const base = `${process.env.PUBLIC_URL}/images/photos/${p.file}`;
  const large = p.large || 1600;
  return (
    <figure className={`photo ${className}`} data-reveal={reveal ? "photo" : undefined}>
      <img
        src={`${base}-${large}.webp`}
        srcSet={`${base}-800.webp 800w, ${base}-${large}.webp ${large}w`}
        sizes={sizes}
        width={p.w}
        height={p.h}
        alt={p.alt}
        loading={priority ? "eager" : "lazy"}
        fetchpriority={priority ? "high" : undefined}
        decoding="async"
        style={p.pos ? { objectPosition: p.pos } : undefined}
      />
    </figure>
  );
}

// Splits a headline into words for the word reveal. Pass lines as an
// array to force line breaks; spaces stay real so it reads naturally.
export function Words({ lines }) {
  let i = 0;
  return lines.map((line, l) => (
    <React.Fragment key={l}>
      {l > 0 && <br />}
      {line.split(" ").map((w, k) => (
        <React.Fragment key={k}>
          {k > 0 && " "}
          <span className="w" style={{ "--i": i++ }}>{w}</span>
        </React.Fragment>
      ))}
    </React.Fragment>
  ));
}

// Pill button: the fill grows across the arrow knob on hover.
// variant: "primary" (brand blue, one per view), "ink" (navy), "light" (white, on dark/photo).
export function PillLink({ to, href, children, variant = "primary", className = "", ...rest }) {
  const cls = `pill pill-${variant} ${className}`;
  const inner = (
    <>
      <span className="pill-fill" aria-hidden="true" />
      <span className="pill-label">{children}</span>
      <span className="pill-knob" aria-hidden="true"><Arrow /></span>
    </>
  );
  if (href) return <a className={cls} href={href} {...rest}>{inner}</a>;
  return <Link className={cls} to={to} {...rest}>{inner}</Link>;
}

export function TextLink({ to, children, dark = false }) {
  return (
    <Link className={`text-link${dark ? " on-dark" : ""}`} to={to}>
      <span>{children}</span>
      <Arrow />
    </Link>
  );
}

// Inner-page hero: heading on paper, with an optional wide photo below.
export function PageHero({ eyebrow, title, lead, photo, children }) {
  return (
    <section className={`page-hero${photo ? " has-photo" : ""}`}>
      <div className="wrap">
        <p className="kicker" data-reveal>{eyebrow}</p>
        <h1 className="display" data-reveal="words"><Words lines={title} /></h1>
        {lead && <p className="lead page-lead" data-reveal style={{ "--d": 3 }}>{lead}</p>}
        {children}
      </div>
      {photo && (
        <div className="wrap">
          <Photo name={photo} priority sizes="(max-width: 1260px) 100vw, 1200px" className="page-hero-photo" />
        </div>
      )}
    </section>
  );
}

export function SectionHead({ kicker, title, children, center = false }) {
  return (
    <div className={`sec-head${center ? " center" : ""}`} data-reveal>
      <p className="kicker">{kicker}</p>
      <h2 className="h2">{title}</h2>
      {children && <p className="lead">{children}</p>}
    </div>
  );
}

const PATHS = [
  { title: "Learn", body: "Join a cohort and build real IT skills with a mentor beside you.", to: "/joinUs", cta: "Join a cohort" },
  { title: "Mentor or volunteer", body: "Share what you know, run a session, or help us reach more communities.", to: "/joinUs", cta: "Give your time" },
  { title: "Partner", body: "Sponsor a cohort, host a programme, or bring a project to our engineers.", to: "/contact", cta: "Partner with us" },
];

// Closing band on most pages: "I am a…" path selection.
export function GetInvolved({ title = "Find your place in the community.", photo = "studentPortrait" }) {
  return (
    <section className="section involve">
      <div className="wrap involve-grid">
        <div className="involve-copy">
          <SectionHead kicker="Get involved" title={title}>
            EMANAC grows through the people in it. Whichever way you come in, you'll be welcome.
          </SectionHead>
          <ul className="paths">
            {PATHS.map((p, i) => (
              <li key={p.title} data-reveal style={{ "--d": i }}>
                <Link to={p.to} className="path">
                  <span className="path-text">
                    <b>{p.title}</b>
                    <span>{p.body}</span>
                  </span>
                  <span className="path-cta">{p.cta}<Arrow /></span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <Photo name={photo} className="involve-photo" sizes="(max-width: 860px) 100vw, 40vw" />
      </div>
    </section>
  );
}
